import React from "react";

export const DIAGNOSTICS_STORAGE_KEY = "grain:diagnostics:performance";
const DIAGNOSTICS_GLOBAL_KEY = "__grainPerformanceDiagnostics";
const DIAGNOSTICS_EVENT_NAME = "grain:diagnostics:update";
const MAX_RECENT_EVENTS = 20;

const listeners = new Set();

let hasHydratedDiagnosticsSnapshot = false;
let hasBoundStorageListener = false;
let diagnosticsSnapshot = buildDiagnosticsSnapshot();

function resolveGlobalScope() {
  return typeof globalThis === "object" && globalThis ? globalThis : null;
}

function resolveDiagnosticsStorage() {
  const globalScope = resolveGlobalScope();
  const storage = globalScope?.localStorage;
  return storage && typeof storage.getItem === "function" && typeof storage.setItem === "function"
    ? storage
    : null;
}

function isDiagnosticsConsoleDebugEnabled() {
  const globalScope = resolveGlobalScope();
  return globalScope?.__grainPerformanceDiagnosticsDebug === true;
}

function normalizeIsoTimestamp(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function normalizeOptionalText(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function normalizeSnapshotStatus(value) {
  return value === "error" || value === "success" ? value : "";
}

function normalizeNonNegativeInteger(value) {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue >= 0 ? parsedValue : 0;
}

function sanitizeDiagnosticsDetails(value) {
  if (value === undefined) {
    return null;
  }

  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return {
      notice: "details_not_serializable",
    };
  }
}

function normalizeRecentDiagnosticsEvents(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .slice(-MAX_RECENT_EVENTS)
    .map((entry) => ({
      at: normalizeIsoTimestamp(entry?.at),
      details: sanitizeDiagnosticsDetails(entry?.details),
      event: typeof entry?.event === "string" ? entry.event.trim() : "",
      scope: typeof entry?.scope === "string" ? entry.scope.trim() : "",
    }))
    .filter((entry) => entry.at && entry.event && entry.scope);
}

function buildDiagnosticsSnapshot(candidate = {}) {
  return Object.freeze({
    activatedAt: normalizeIsoTimestamp(candidate.activatedAt),
    diagnosticsSessionId: normalizeOptionalText(candidate.diagnosticsSessionId),
    eventCount: normalizeNonNegativeInteger(candidate.eventCount),
    isActive: candidate.isActive === true,
    lastEventAt: normalizeIsoTimestamp(candidate.lastEventAt),
    lastEventLabel: typeof candidate.lastEventLabel === "string" ? candidate.lastEventLabel.trim() : "",
    lastSnapshotAt: normalizeIsoTimestamp(candidate.lastSnapshotAt),
    lastSnapshotError: normalizeOptionalText(candidate.lastSnapshotError),
    lastSnapshotId: normalizeOptionalText(candidate.lastSnapshotId),
    lastSnapshotStatus: normalizeSnapshotStatus(candidate.lastSnapshotStatus),
    lastSnapshotStoragePath: normalizeOptionalText(candidate.lastSnapshotStoragePath),
    lastToggledAt: normalizeIsoTimestamp(candidate.lastToggledAt),
    nextSnapshotAt: normalizeIsoTimestamp(candidate.nextSnapshotAt),
    recentEvents: Object.freeze(normalizeRecentDiagnosticsEvents(candidate.recentEvents)),
    resetCount: normalizeNonNegativeInteger(candidate.resetCount),
    snapshotCount: normalizeNonNegativeInteger(candidate.snapshotCount),
  });
}

function createDiagnosticsSessionId() {
  const globalScope = resolveGlobalScope();
  if (typeof globalScope?.crypto?.randomUUID === "function") {
    return globalScope.crypto.randomUUID();
  }

  return `diag-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

function syncDiagnosticsSnapshotToStorage() {
  const storage = resolveDiagnosticsStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(DIAGNOSTICS_STORAGE_KEY, JSON.stringify(diagnosticsSnapshot));
  } catch {}
}

function syncDiagnosticsSnapshotToDevTools() {
  const globalScope = resolveGlobalScope();
  if (!globalScope) {
    return;
  }

  globalScope[DIAGNOSTICS_GLOBAL_KEY] = diagnosticsSnapshot;
  if (typeof globalScope.dispatchEvent !== "function" || typeof globalScope.CustomEvent !== "function") {
    return;
  }

  try {
    globalScope.dispatchEvent(new globalScope.CustomEvent(DIAGNOSTICS_EVENT_NAME, {
      detail: diagnosticsSnapshot,
    }));
  } catch {}
}

function notifyDiagnosticsListeners() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {}
  });
}

function publishDiagnosticsSnapshot({ notify = true, persist = true } = {}) {
  if (persist) {
    syncDiagnosticsSnapshotToStorage();
  }
  syncDiagnosticsSnapshotToDevTools();
  if (notify) {
    notifyDiagnosticsListeners();
  }
}

function handleDiagnosticsStorageEvent(event) {
  if (event?.key !== DIAGNOSTICS_STORAGE_KEY) {
    return;
  }

  try {
    diagnosticsSnapshot = buildDiagnosticsSnapshot(event.newValue ? JSON.parse(event.newValue) : null);
  } catch {
    diagnosticsSnapshot = buildDiagnosticsSnapshot();
  }

  publishDiagnosticsSnapshot({ notify: true, persist: false });
}

function ensureDiagnosticsStorageListener() {
  if (hasBoundStorageListener) {
    return;
  }

  const globalScope = resolveGlobalScope();
  if (!globalScope || typeof globalScope.addEventListener !== "function") {
    return;
  }

  globalScope.addEventListener("storage", handleDiagnosticsStorageEvent);
  hasBoundStorageListener = true;
}

function hydrateDiagnosticsSnapshot() {
  if (hasHydratedDiagnosticsSnapshot) {
    return;
  }

  hasHydratedDiagnosticsSnapshot = true;
  const storage = resolveDiagnosticsStorage();
  if (!storage) {
    publishDiagnosticsSnapshot({ notify: false, persist: false });
    return;
  }

  try {
    diagnosticsSnapshot = buildDiagnosticsSnapshot(JSON.parse(storage.getItem(DIAGNOSTICS_STORAGE_KEY) || "null"));
  } catch {
    diagnosticsSnapshot = buildDiagnosticsSnapshot();
  }

  ensureDiagnosticsStorageListener();
  publishDiagnosticsSnapshot({ notify: false, persist: false });
}

function updateDiagnosticsSnapshot(nextSnapshot) {
  diagnosticsSnapshot = buildDiagnosticsSnapshot(nextSnapshot);
  ensureDiagnosticsStorageListener();
  publishDiagnosticsSnapshot();
  return diagnosticsSnapshot;
}

export function subscribeToDiagnostics(listener) {
  hydrateDiagnosticsSnapshot();
  if (typeof listener !== "function") {
    return () => {};
  }

  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getDiagnosticsSnapshot() {
  hydrateDiagnosticsSnapshot();
  return diagnosticsSnapshot;
}

export function isDiagnosticsActive() {
  return getDiagnosticsSnapshot().isActive;
}

export function setDiagnosticsActive(nextIsActive) {
  hydrateDiagnosticsSnapshot();

  const isActive = nextIsActive === true;
  const currentSnapshot = diagnosticsSnapshot;
  const timestamp = new Date().toISOString();
  return updateDiagnosticsSnapshot(
    isActive
      ? {
        ...currentSnapshot,
        activatedAt: currentSnapshot.isActive ? currentSnapshot.activatedAt || timestamp : timestamp,
        diagnosticsSessionId: currentSnapshot.isActive ? currentSnapshot.diagnosticsSessionId : createDiagnosticsSessionId(),
        eventCount: currentSnapshot.isActive ? currentSnapshot.eventCount : 0,
        isActive: true,
        lastEventAt: currentSnapshot.isActive ? currentSnapshot.lastEventAt : null,
        lastEventLabel: currentSnapshot.isActive ? currentSnapshot.lastEventLabel : "",
        lastSnapshotAt: currentSnapshot.isActive ? currentSnapshot.lastSnapshotAt : null,
        lastSnapshotError: currentSnapshot.isActive ? currentSnapshot.lastSnapshotError : "",
        lastSnapshotId: currentSnapshot.isActive ? currentSnapshot.lastSnapshotId : "",
        lastSnapshotStatus: currentSnapshot.isActive ? currentSnapshot.lastSnapshotStatus : "",
        lastSnapshotStoragePath: currentSnapshot.isActive ? currentSnapshot.lastSnapshotStoragePath : "",
        lastToggledAt: timestamp,
        nextSnapshotAt: currentSnapshot.isActive ? currentSnapshot.nextSnapshotAt : timestamp,
        recentEvents: currentSnapshot.isActive ? [...currentSnapshot.recentEvents] : [],
        resetCount: currentSnapshot.isActive ? currentSnapshot.resetCount : 0,
        snapshotCount: currentSnapshot.isActive ? currentSnapshot.snapshotCount : 0,
      }
      : {
        ...currentSnapshot,
        isActive: false,
        lastToggledAt: timestamp,
        nextSnapshotAt: null,
      },
  );
}

export function setDiagnosticsNextSnapshotAt(nextSnapshotAt) {
  const currentSnapshot = getDiagnosticsSnapshot();
  const normalizedNextSnapshotAt = normalizeIsoTimestamp(nextSnapshotAt);
  if (currentSnapshot.nextSnapshotAt === normalizedNextSnapshotAt) {
    return currentSnapshot;
  }

  return updateDiagnosticsSnapshot({
    ...currentSnapshot,
    nextSnapshotAt: normalizedNextSnapshotAt,
  });
}

export function recordDiagnosticsSnapshotResult({
  capturedAt = null,
  errorMessage = "",
  nextSnapshotAt = null,
  snapshotId = "",
  status = "success",
  storageRelativePath = "",
} = {}) {
  const currentSnapshot = getDiagnosticsSnapshot();
  const resolvedStatus = status === "error" ? "error" : "success";
  const resolvedCapturedAt = normalizeIsoTimestamp(capturedAt) || new Date().toISOString();

  return updateDiagnosticsSnapshot({
    ...currentSnapshot,
    lastSnapshotAt: resolvedStatus === "success" ? resolvedCapturedAt : currentSnapshot.lastSnapshotAt,
    lastSnapshotError: resolvedStatus === "error" ? normalizeOptionalText(errorMessage) : "",
    lastSnapshotId: resolvedStatus === "success" ? normalizeOptionalText(snapshotId) : currentSnapshot.lastSnapshotId,
    lastSnapshotStatus: resolvedStatus,
    lastSnapshotStoragePath: resolvedStatus === "success"
      ? normalizeOptionalText(storageRelativePath)
      : currentSnapshot.lastSnapshotStoragePath,
    nextSnapshotAt: normalizeIsoTimestamp(nextSnapshotAt),
    snapshotCount: resolvedStatus === "success"
      ? currentSnapshot.snapshotCount + 1
      : currentSnapshot.snapshotCount,
  });
}

export function recordDiagnosticsEvent(scope, event, details = {}, { countAsReset = false } = {}) {
  if (!isDiagnosticsActive()) {
    return getDiagnosticsSnapshot();
  }

  const currentSnapshot = getDiagnosticsSnapshot();
  const timestamp = new Date().toISOString();
  const resolvedScope = typeof scope === "string" && scope.trim() ? scope.trim() : "diagnostics";
  const resolvedEvent = typeof event === "string" && event.trim() ? event.trim() : "event";
  const sanitizedDetails = sanitizeDiagnosticsDetails(details);
  const nextSnapshot = updateDiagnosticsSnapshot({
    ...currentSnapshot,
    eventCount: currentSnapshot.eventCount + 1,
    lastEventAt: timestamp,
    lastEventLabel: `${resolvedScope}:${resolvedEvent}`,
    recentEvents: [
      ...currentSnapshot.recentEvents,
      {
        at: timestamp,
        details: sanitizedDetails,
        event: resolvedEvent,
        scope: resolvedScope,
      },
    ].slice(-MAX_RECENT_EVENTS),
    resetCount: countAsReset ? currentSnapshot.resetCount + 1 : currentSnapshot.resetCount,
  });

  if (isDiagnosticsConsoleDebugEnabled() && typeof console !== "undefined" && typeof console.log === "function") {
    console.log(`[PHASE 1.5 TEMP] ${resolvedScope}`, {
      ...details,
      diagnostics: {
        eventCount: nextSnapshot.eventCount,
        isActive: nextSnapshot.isActive,
        resetCount: nextSnapshot.resetCount,
      },
      event: resolvedEvent,
    });
  }

  return nextSnapshot;
}

export function resetDiagnosticsStoreForTests() {
  hasHydratedDiagnosticsSnapshot = true;
  diagnosticsSnapshot = buildDiagnosticsSnapshot();
  const storage = resolveDiagnosticsStorage();
  if (storage && typeof storage.removeItem === "function") {
    try {
      storage.removeItem(DIAGNOSTICS_STORAGE_KEY);
    } catch {}
  }
  publishDiagnosticsSnapshot();
}

export default function useDiagnostics() {
  const snapshot = React.useSyncExternalStore(
    subscribeToDiagnostics,
    getDiagnosticsSnapshot,
    getDiagnosticsSnapshot,
  );

  return React.useMemo(() => ({
    ...snapshot,
    logEvent: recordDiagnosticsEvent,
    recordSnapshotResult: recordDiagnosticsSnapshotResult,
    setActive: setDiagnosticsActive,
    setNextSnapshotAt: setDiagnosticsNextSnapshotAt,
  }), [snapshot]);
}